/** Branded handler for e-mail action links (set/reset password) at /auth/action,
 * so users never see provider-branded pages (plan §7.3). */
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { friendlyAuthError, useAuth } from "../auth/AuthContext";
import { Field, Notice, Spinner } from "../components/ui";

export function AuthActionPage() {
  const [params] = useSearchParams();
  const { ready, verifyResetCode, confirmReset, signIn } = useAuth();
  const navigate = useNavigate();
  const mode = params.get("mode");
  const code = params.get("oobCode") ?? "";
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!ready || mode !== "resetPassword" || !code) return;
    verifyResetCode(code)
      .then(setEmail)
      .catch((reason) => setError(friendlyAuthError(reason)));
  }, [ready, mode, code, verifyResetCode]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < 12) return setError("Choose a password of at least 12 characters.");
    if (password !== repeat) return setError("The two passwords do not match.");
    setBusy(true);
    setError(null);
    try {
      await confirmReset(code, password);
      if (email) await signIn(email, password);
      navigate("/", { replace: true });
    } catch (reason) {
      setError(friendlyAuthError(reason));
    } finally {
      setBusy(false);
    }
  }

  if (mode !== "resetPassword") {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <Notice kind="warning">This link is not supported. </Notice>
          <Link to="/login">Go to sign in</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="auth-page">
      <form className="auth-card stack" onSubmit={submit}>
        <img src="/nsgs-logo.png" alt="NSGS" />
        <h1 style={{ fontSize: "1.45rem" }}>Set your SAT Analyzer password</h1>
        {!email && !error && <Spinner label="Checking the link" />}
        {email && <p className="muted small">For {email}. Use at least 12 characters.</p>}
        {error && (
          <Notice kind="error">
            {error} <Link to="/login">Go to sign in</Link>
          </Notice>
        )}
        {email && (
          <>
            <Field label="New password">
              <input className="input" type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <Field label="Repeat the password">
              <input className="input" type="password" autoComplete="new-password" minLength={12} required value={repeat} onChange={(e) => setRepeat(e.target.value)} />
            </Field>
            <button className="btn primary block" disabled={busy}>
              {busy ? "Saving…" : "Save password and sign in"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
